import { Directive, ElementRef, forwardRef, HostListener, Inject, Input } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { MAT_INPUT_VALUE_ACCESSOR } from "@angular/material/input";

const noop = () => {
};

@Directive({
  selector: "input[safeTextInput]",
  providers: [
    {provide: MAT_INPUT_VALUE_ACCESSOR, useExisting: SafeTextInputDirective}
  ]
})

export class SafeTextInputDirective implements ControlValueAccessor {

  @Input()
  public maxLength = 0;

  protected _elementRef: ElementRef;

  // The internal data model
  protected _innerValue: string;

  protected _previousValue: string;

  protected _isDisabled = false;

  // Placeholders for the callbacks which are later provided
  // by the Control Value Accessor
  protected onTouchedCallback: () => void = noop;
  protected onChangeCallback: (_: any) => void = noop;

  constructor(private elementRef: ElementRef) {
    this._elementRef = elementRef;
  }

  // get accessor
  get value(): string {
    return this._innerValue ? this._innerValue : null;
  }

  // set accessor including call the onchange callback
  set value(v: string) {
    if (v !== this._innerValue) {
      this._innerValue = v;
      this._previousValue = v;
      this.onChangeCallback(this._innerValue);
    }
  }

  // From ControlValueAccessor interface
  writeValue(value: string) {
    const tempValue: string = this.hasValue(value) ? value : null;
    if (tempValue !== this._innerValue) {
      this._innerValue = tempValue;
      this.setNativeElementValue();
    }
  }

  // From ControlValueAccessor interface
  registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  // From ControlValueAccessor interface
  registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }

  // From ControlValueAccessor interface
  setDisabledState(isDisabled: boolean): void {
    this._isDisabled = isDisabled;
  }

  @HostListener("keypress", ["$event"])
  onKeyPress(event: Event) {
    const currentValue: string = (<HTMLInputElement>event.currentTarget).value;
    if (this.maxLength && currentValue.length > (this.maxLength - 1)) {
      event.stopPropagation();
      return false;
    }
  }

  @HostListener("input", ["$event"])
  onInput(event: Event) {
    const currentValue: string = (<HTMLInputElement>event.currentTarget).value;
    this._elementRef.nativeElement.value
      = currentValue.replace(/[^A-Za-z0-9 öüóőúéáűíÖÜÓŐÚÉÁŰÍ\.\,\:\-\_\(\)\&]/g, "");
  }

  // Set touched on blur
  @HostListener("blur", ["$event"])
  onBlur() {
    this._previousValue = this._innerValue;
    this._innerValue = this.hasValue(this.elementRef.nativeElement.value) ? this.elementRef.nativeElement.value : null;
    this.setNativeElementValue();
    if (!this._innerValue || this._innerValue !== this._previousValue) {
      this.onTouchedCallback();
      this.onChangeCallback(this._innerValue);
    }
  }

  @HostListener("focus", ["$event"])
  onFocus() {
    this.elementRef.nativeElement.value = this._innerValue !== null && this._innerValue !== undefined ? this._innerValue : "";
  }

  protected setNativeElementValue(): void {
    this.elementRef.nativeElement.value = this._innerValue !== null ? this._innerValue : "";
  }

  protected hasValue(value: string): boolean {
    return value !== null && value !== "";
  }
}
